import { Tooltip } from "bootstrap";
import confetti from "canvas-confetti";
import { sendAppsignalError } from "../modules/utils";

export default class Qv {
  constructor(node) {
    this.element = node;

    this.levels = ["Earth", "Mercury", "Mars", "Neptune", "Saturn", "Jupiter"];

    this.calculatedLevel = null;
    this.currentInfoWrapper = this.element.querySelector(".js-current-qv-info-wrapper");
    this.qvMessage = this.element.querySelector(".js-qv-message");
    this.totalQvTooltip = this.element.querySelector(".js-qv-total-tooltip");
    this.totalQvTooltipInstance = new Tooltip(this.totalQvTooltip);

    this.crescent = confetti.shapeFromPath({
      path: "M0.665555 9.41054C0.187262 9.68669 0.0193159 10.302 0.343704 10.749C0.88578 11.4959 1.53381 12.1622 2.26876 12.7262C3.26873 13.4935 4.41004 14.0563 5.62752 14.3825C6.84501 14.7087 8.11482 14.792 9.36447 14.6275C10.6141 14.4629 11.8191 14.0539 12.9107 13.4237C14.0023 12.7935 14.959 11.9544 15.7263 10.9544C16.4936 9.95447 17.0565 8.81316 17.3827 7.59568C17.7089 6.37819 17.7921 5.10838 17.6276 3.85873C17.5067 2.94026 17.2537 2.04591 16.8779 1.20299C16.653 0.698574 16.0361 0.536358 15.5578 0.8125L12.436 2.61487C11.9577 2.89102 11.8079 3.50235 11.9569 4.03416C12.0079 4.2162 12.046 4.40203 12.0708 4.59029C12.1393 5.11021 12.1046 5.63853 11.9689 6.14506C11.8332 6.6516 11.599 7.12645 11.2798 7.54249C10.9605 7.95853 10.5625 8.30762 10.1083 8.56982C9.65418 8.83203 9.15283 9.00221 8.63291 9.07066C8.11299 9.13911 7.58468 9.10448 7.07814 8.96876C6.5716 8.83303 6.09675 8.59886 5.68071 8.27962C5.53006 8.16402 5.38819 8.03809 5.25604 7.9029C4.86999 7.50796 4.26565 7.33203 3.78735 7.60817L0.665555 9.41054Z",
    });

    this.smallRectangle = confetti.shapeFromPath({
      path: "M0 7.64V1C0 0.447715 0.447715 0 1 0H6C6.55228 0 7 0.447714 7 0.999999V7.64C7 8.19229 6.55228 8.64 6 8.64H1C0.447715 8.64 0 8.19229 0 7.64Z",
    });

    this.bigRectangle = confetti.shapeFromPath({
      path: "M0 17.2V1C0 0.447715 0.447715 0 1 0H6C6.55228 0 7 0.447714 7 0.999999V17.2C7 17.7523 6.55228 18.2 6 18.2H1C0.447715 18.2 0 17.7523 0 17.2Z",
    });

    this.confettiOptions = {
      disableForReducedMotion: true,
      shapes: ["circle", this.crescent, this.smallRectangle, this.bigRectangle],
      colors: ["#EB09D4", "#EB6209", "#DCE9C4", "#09B5EB", "#116A80", "#6B2577", "#6B4926", "#657F71"],
      scalar: 1.5,
      particleCount: 75,
      spread: 65,
      startVelocity: 45,
    };

    // Initial
    this.initialLevel = this.element.querySelector(".js-qv-initial-level");
    this.initialQv = this.element.querySelector(".js-qv-initial-qv");
    this.initialDiscount = this.element.querySelector(".js-qv-initial-discount");
    this.initialProgressBar = this.element.querySelector(".js-qv-initial-progress-bar");
    this.initialTooltipInstance = new Tooltip(this.element.querySelector(".js-qv-initial-tooltip"));

    // Current
    this.currentLevel = this.element.querySelector(".js-qv-current-level");
    this.currentQv = this.element.querySelector(".js-qv-current-qv");
    this.currentDiscount = this.element.querySelector(".js-qv-current-discount");
    this.currentProgressBar = this.element.querySelector(".js-qv-current-progress-bar");
    this.currentTooltipInstance = new Tooltip(this.element.querySelector(".js-qv-current-tooltip"));

    // Upcoming
    this.upcomingLevel = this.element.querySelector(".js-qv-upcoming-level");
    this.upcomingQv = this.element.querySelector(".js-qv-upcoming-qv");
    this.upcomingDiscount = this.element.querySelector(".js-qv-upcoming-discount");
    this.upcomingTooltip = this.element.querySelector(".js-qv-upcoming-tooltip");
    this.upcomingTooltipInstance = new Tooltip(this.upcomingTooltip);

    this.timeout;
    this.url = new URL(this.element.dataset.url, window.location.origin);
    this.initialized = false;
    this.displayedFinalConfetti = false;
    this.shouldIgnorePageConfetti = this.element.dataset.ignorePageConfetti === "true";

    this.initialize();
  }

  initialize() {
    this.fetchQvData();

    this.element.addEventListener("qv-update", () => {
      this.fetchQvData();
    });
  }

  fetchQvData() {
    fetch(this.url, {
      retries: 3,
      retryDelay: 1000,
    })
      .then((response) => {
        this.response = response;

        if (response.status === 200) {
          return response.json();
        }

        throw new Error("Something went wrong on api server!");
      })
      .then((data) => {
        this.buildQvComponent(data);

        if (!this.initialized) {
          // We build the Qv component once, so we avoid double fetch request for the updated prices.
          // Incase the achieved level is higher than the initial level
          this.initialized = true;
        }
      })
      .catch((e) => {
        console.error(e);
        sendAppsignalError(e, "Qv", {
          response: this.response,
        });
      });
  }

  buildQvComponent(data) {
    const { upcomingLevelMessage, qvMessage, tooltipTotalQvMessage, initialQv, currentQv, upcomingQv } = data;

    this.element.dataset.upcomingLevelMessage = upcomingLevelMessage;
    this.qvMessage.innerText = qvMessage;

    this.setInitialValues(initialQv);
    this.setUpcomingValues(upcomingQv);

    this.totalQvTooltipInstance.setContent({ ".tooltip-inner": tooltipTotalQvMessage });

    if (this.calculatedLevel === null) {
      this.calculatedLevel = initialQv.level;
    }

    if (currentQv.level === initialQv.level) {
      if (this.wasTransitioned) {
        // We transitioned, but the level is the same as the initial level, so we need to transition back
        this.calculatedLevel = currentQv.level;
        // Index of current and initial level is 0, so we pass [0, 0] to transition
        this.transition([0, 0]);

        window.dispatchEvent(new Event("update-prices"));
      }
      this.setCurrentValues(currentQv, true);
      window.dispatchEvent(new Event("qv:initial-level-reached"));

      return;
    }

    if (this.calculatedLevel !== currentQv.level) {
      // We need to transition to the new level and update the values accordingly

      // We need to update all product prices and summary

      if (this.initialized) {
        window.dispatchEvent(new Event("update-prices"));
      }

      // use this.levels to check if it's progress or regress
      const currentLevelIndex = this.levels.indexOf(currentQv.level);
      const calculatedLevelIndex = this.levels.indexOf(this.calculatedLevel);
      this.calculatedLevel = currentQv.level;

      this.transition([currentLevelIndex, calculatedLevelIndex]);
      this.setCurrentValues(currentQv);

      this.wasTransitioned = true;
    } else {
      // We need to update the values only, without transitioning to a new level.
      this.setCurrentValues(currentQv);
    }

    window.dispatchEvent(new Event("qv:level-reached"));
  }

  transition(array) {
    clearTimeout(this.timeout);

    this.element.classList.remove("final-level");
    this.element.classList.add("transition");

    if (array[0] === array[1]) {
      this.element.setAttribute("data-content", this.element.dataset.initialLevelMessage);
      this.currentInfoWrapper.classList.add("d-none");
      this.element.classList.remove("show-second-progress-bar");

      this.wasTransitioned = false;
    } else {
      this.currentInfoWrapper.classList.remove("d-none");
      this.element.classList.add("show-second-progress-bar");

      if (array[1] > array[0]) {
        this.element.setAttribute("data-content", this.element.dataset.upcomingLevelMessage);
      } else {
        this.element.setAttribute("data-content", this.element.dataset.upcomingLevelMessage);
        this.displayConfetti();
      }
    }

    this.timeout = setTimeout(() => {
      this.element.classList.add("transition-end");
      setTimeout(() => {
        this.element.classList.remove("transition");
        this.element.classList.remove("transition-end");
      }, 500);
    }, 3500);
  }

  setInitialValues(data) {
    const { level, qv, progressPercentage, discountPercentage, toolTipText } = data;

    this.initialLevel.textContent = level;
    this.initialQv.textContent = qv;
    this.initialDiscount.textContent = discountPercentage;
    this.initialProgressBar.style.width = `${progressPercentage}%`;
    this.initialTooltipInstance.setContent({ ".tooltip-inner": toolTipText });
  }

  setCurrentValues(data, isInitial = false) {
    const { level, qv, discountPercentage, toolTipText, progressPercentage } = data;

    if (isInitial) {
      // If the current level is the same as the initial level, we don't need to update the current level values just the "initial"
      this.initialProgressBar.style.width = `${progressPercentage}%`;
      return;
    }

    if (level === "Jupiter" && progressPercentage === 100) {
      // We reached the final level and the progress bar is full
      clearTimeout(this.timeout);
      this.element.classList.add("final-level");

      if (!this.displayedFinalConfetti) {
        this.displayConfetti(true);
      }
      return;
    }

    this.currentLevel.textContent = level;
    this.currentQv.textContent = qv;
    this.currentDiscount.textContent = discountPercentage;
    this.currentProgressBar.style.width = `${progressPercentage}%`;
    this.currentTooltipInstance.setContent({ ".tooltip-inner": toolTipText });
  }

  setUpcomingValues(data) {
    const { level, qv, discountPercentage, toolTipText } = data;

    this.upcomingLevel.textContent = level;
    this.upcomingDiscount.textContent = discountPercentage;
    this.upcomingQv.textContent = qv;
    this.upcomingTooltipInstance.setContent({ ".tooltip-inner": toolTipText });
  }

  createCanvasElement() {
    const canvasElement = document.createElement("canvas");
    this.element.insertBefore(canvasElement, this.element.firstChild);
    canvasElement.classList.add("confetti-canvas");
    return canvasElement;
  }

  displayConfetti(finalCelebration = false) {
    if (finalCelebration && this.shouldIgnorePageConfetti) {
      return;
    }

    var canvasElement = this.createCanvasElement();

    var myConfetti = confetti.create(canvasElement, {
      resize: true,
    });

    if (finalCelebration) {
      this.displayedFinalConfetti = true;

      const duration = 5 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = {
        startVelocity: 30,
        spread: 360,
        ticks: 60,
        zIndex: 1200,
        shapes: ["circle", this.crescent, this.smallRectangle, this.bigRectangle],
        colors: ["#EB09D4", "#EB6209", "#DCE9C4", "#09B5EB"],
        scalar: 1.5,
      };

      const interval = setInterval(function () {
        let timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        let particleCount = 50 * (timeLeft / duration);

        // We let the confetti library handle the creation and deletion of the canvas element.
        confetti({ ...defaults, particleCount, origin: { x: Math.random() * (0.3 - 0.1) + 0.1, y: Math.random() - 0.2 } });
        confetti({ ...defaults, particleCount, origin: { x: Math.random() * (0.9 - 0.7) + 0.7, y: Math.random() - 0.2 } });
      }, 250);
    }

    myConfetti({
      ...this.confettiOptions,
      angle: 145,
      origin: { x: 1 },
    });

    myConfetti({
      ...this.confettiOptions,
      angle: 35,
      origin: { x: 0 },
    }).then(() => {
      // Since this is the last confetti, we need to remove the canvas element
      canvasElement.remove();
    });
  }
}
